import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  History,
  Close,
  AddCircleOutline,
  Autorenew,
  QrCodeScanner,
  CompareArrows,
  ArrowRightAlt,
} from "@mui/icons-material";
import moment from "moment-timezone";
import { GetPackageLogs } from "api/packages";
import { useTranslation } from "react-i18next";
import KpiTile from "components/bento/KpiTile";
import EmptyState from "components/bento/EmptyState";
import {
  MONO,
  TEXT_DARK,
  TEXT_BODY,
  TEXT_SECONDARY,
  TEXT_SUBTLE,
  BG_PURPLE_GRADIENT,
  BORDER,
  BORDER_PURPLE,
  DIVIDER,
  PURPLE_LIGHT,
  PURPLE_MAIN,
  PURPLE_DARK,
  PURPLE_TINT_100,
  RADIUS_CARD,
  RADIUS_TILE,
  cardLabelSx,
} from "components/bento/bentoTokens";

// Per actionType visual identity — drives both the small badge pill (always
// colored by type) and the timeline icon circle (bold/solid for the latest
// entry, muted tint for older ones — same language as the Station History
// timeline in TrackingDetail).
const ACTION_META = {
  Create: { color: "#16a34a", badgeBg: "#dcfce7", badgeCol: "#15803d", badgeBorder: "#86efac", Icon: AddCircleOutline },
  Update: { color: PURPLE_LIGHT, badgeBg: PURPLE_TINT_100, badgeCol: PURPLE_DARK, badgeBorder: BORDER_PURPLE, Icon: Autorenew },
  scan: { color: "#6b6775", badgeBg: "#f8f7fa", badgeCol: "#6b6775", badgeBorder: "#e6e3ec", Icon: QrCodeScanner },
  match: { color: "#4338ca", badgeBg: "#eef2ff", badgeCol: "#4338ca", badgeBorder: "#c7d2fe", Icon: CompareArrows },
};
const DEFAULT_ACTION_META = { color: "#6b6775", badgeBg: "#f8f7fa", badgeCol: "#6b6775", badgeBorder: "#e6e3ec", Icon: History };

// Normalizes the various shapes `Log.description` can take (per-field
// {from,to} object, array of {field,value}, or a plain message) into either
// a list of diff rows or a standalone message string.
function getDiffInfo(description) {
  if (!description) return { rows: [], message: null };

  if (Array.isArray(description)) {
    if (description.length === 0) return { rows: [], message: null };
    const rows = description.map((item, i) => {
      if (item && typeof item === "object" && "field" in item) {
        return { field: item.field, from: undefined, to: item.value };
      }
      return { field: `Item ${i + 1}`, from: undefined, to: JSON.stringify(item) };
    });
    return { rows, message: null };
  }

  if (typeof description === "object") {
    const entries = Object.entries(description);
    if (entries.length === 0) return { rows: [], message: "No changes recorded" };
    const rows = entries.map(([field, val]) => {
      if (val && typeof val === "object" && "from" in val && "to" in val) {
        return { field, from: val.from, to: val.to };
      }
      return { field, from: undefined, to: val };
    });
    return { rows, message: null };
  }

  return { rows: [], message: String(description) };
}

function getValueChipStyle(field, to) {
  if (field === "station") {
    return { color: PURPLE_DARK, bg: PURPLE_TINT_100, border: BORDER_PURPLE };
  }
  const str = String(to);
  if (typeof to === "boolean" || str === "true" || str === "false") {
    return to === true || str === "true"
      ? { color: "#15803d", bg: "#dcfce7", border: "#86efac" }
      : { color: TEXT_BODY, bg: "#f0fdf4", border: "#86efac" };
  }
  return { color: TEXT_BODY, bg: "#f0fdf4", border: "#86efac" };
}

function formatVal(v) {
  if (v && typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function DiffRow({ field, from, to }) {
  const hasFrom = from !== undefined && from !== null && from !== "";
  const hasTo = to !== undefined && to !== null && to !== "";
  const toStyle = getValueChipStyle(field, to);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "150px 1fr" },
        alignItems: "start",
        gap: "6px",
        p: "11px 18px",
        borderBottom: `1px solid ${DIVIDER}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px", pr: "16px" }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: field === "station" ? PURPLE_LIGHT : "#c4b5fd", flexShrink: 0 }} />
        <Typography sx={{ fontFamily: MONO, fontSize: "12px", fontWeight: 600, color: TEXT_SECONDARY }}>{field}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        {hasFrom && (
          <Box
            component="span"
            sx={{
              fontFamily: MONO, fontSize: "12.5px", fontWeight: 600, color: "#c0343a",
              background: "#fff1f2", border: "1px solid #fca5a5", px: "9px", py: "3px",
              borderRadius: "7px", textDecoration: "line-through", maxWidth: 280,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {formatVal(from)}
          </Box>
        )}
        {hasFrom && hasTo && <ArrowRightAlt sx={{ fontSize: 15, color: "#c3bfcd" }} />}
        {hasTo && (
          <Box
            component="span"
            sx={{
              fontFamily: MONO, fontSize: "12.5px", fontWeight: 600, color: toStyle.color,
              background: toStyle.bg, border: `1px solid ${toStyle.border}`, px: "9px", py: "3px",
              borderRadius: "7px", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatVal(to)}
          </Box>
        )}
        {!hasFrom && !hasTo && <Typography sx={{ fontSize: "12.5px", color: TEXT_SUBTLE, fontStyle: "italic" }}>—</Typography>}
      </Box>
    </Box>
  );
}

const PackageEditLogsDialog = ({ open, onClose, packageId, tracking }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && packageId) {
      setLoading(true);
      GetPackageLogs(packageId).then((res) => {
        setLogs(res.data || []);
        setLoading(false);
      });
    }
  }, [open, packageId]);

  const actorCount = useMemo(() => new Set(logs.map((l) => l.actionByUser)).size, [logs]);
  const updatesCount = useMemo(() => logs.filter((l) => l.actionType === "Update").length, [logs]);
  const scansCount = useMemo(() => logs.filter((l) => l.actionType === "scan").length, [logs]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: RADIUS_CARD, overflow: "hidden" } }}>
      <DialogTitle sx={{ p: "24px 28px 20px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "12px", flexShrink: 0,
                background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <History sx={{ fontSize: 19, color: PURPLE_MAIN }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: TEXT_DARK, letterSpacing: "-.01em" }}>
                {t("packageEditLogs.title")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "2px", flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: MONO, fontSize: "13px", fontWeight: 600, color: PURPLE_DARK }}>
                  {tracking}
                </Typography>
                {!loading && (
                  <Typography sx={{ fontSize: "12px", color: TEXT_SUBTLE }}>
                    · {logs.length} {logs.length === 1 ? "change" : "changes"} · {actorCount} {actorCount === 1 ? "actor" : "actors"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Close sx={{ fontSize: 14 }} />}
            onClick={onClose}
            sx={{ borderColor: BORDER_PURPLE, borderRadius: "12px", fontWeight: 700, fontSize: "13px", px: "16px" }}
          >
            {t("common.close")}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: "0 28px 28px" }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress color="secondary" />
          </Box>
        )}

        {!loading && logs.length === 0 && (
          <EmptyState icon={<History sx={{ fontSize: 28, color: TEXT_SUBTLE }} />} message={t("packageEditLogs.noLogs")} />
        )}

        {!loading && logs.length > 0 && (
          <>
            {/* ══ STATS STRIP ══ */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", mb: "24px" }}>
              <Box sx={{ background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`, borderRadius: RADIUS_TILE, p: "16px 20px" }}>
                <Box sx={cardLabelSx}>Total Edits</Box>
                <Typography sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: PURPLE_DARK, mt: "6px" }}>
                  {logs.length}
                </Typography>
              </Box>
              <KpiTile label="Updates" value={updatesCount} />
              <KpiTile label="Scans" value={scansCount} />
            </Box>

            {/* ══ TIMELINE ══ */}
            <Box>
              {logs.map((log, index) => {
                const meta = ACTION_META[log.actionType] || DEFAULT_ACTION_META;
                const { Icon } = meta;
                const isLatest = index === 0;
                const hasLine = index < logs.length - 1;
                const { rows, message } = getDiffInfo(log.description);

                return (
                  <Box key={log._id} sx={{ display: "flex", gap: "18px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 48 }}>
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          background: isLatest ? meta.color : `${meta.color}14`,
                          border: isLatest ? `3px solid ${meta.color}` : `2px solid ${meta.color}55`,
                          boxShadow: isLatest ? `0 4px 14px -4px ${meta.color}99` : "none",
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: isLatest ? "#fff" : meta.color }} />
                      </Box>
                      {hasLine && (
                        <Box sx={{ width: "2px", flex: 1, minHeight: 20, background: `linear-gradient(180deg, ${BORDER_PURPLE} 0%, transparent 100%)`, my: "4px", borderRadius: "2px" }} />
                      )}
                    </Box>

                    <Box sx={{ flex: 1, pb: "20px", minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", mb: "10px", flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "12px",
                              fontWeight: 700, px: "11px", py: "5px", borderRadius: "999px",
                              background: meta.badgeBg, color: meta.badgeCol, border: `1px solid ${meta.badgeBorder}`,
                            }}
                          >
                            {log.actionType}
                          </Box>
                          {log.remark && (
                            <Typography sx={{ fontSize: "12px", color: TEXT_SUBTLE, fontStyle: "italic" }}>
                              {log.remark}
                            </Typography>
                          )}
                          <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: TEXT_SECONDARY, fontWeight: 500 }}>
                            {moment(log.createdAt).tz("Asia/Vientiane").format("DD/MM/YYYY · HH:mm:ss")}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: PURPLE_MAIN, fontWeight: 600 }}>
                          {log.actionByUser}
                        </Typography>
                      </Box>

                      {rows.length > 0 && (
                        <Box sx={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "16px", overflow: "hidden" }}>
                          {rows.map((row, i) => (
                            <DiffRow key={`${row.field}-${i}`} field={row.field} from={row.from} to={row.to} />
                          ))}
                        </Box>
                      )}
                      {!rows.length && message && (
                        <Typography sx={{ fontSize: "13px", color: TEXT_SUBTLE, fontStyle: "italic" }}>
                          {message}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PackageEditLogsDialog;
